// Firebase initialization - runs at app entry before any routes
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';

let _app: FirebaseApp | null = null;
let _didConnectEmu = false;

function isLocalHost(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return (
    normalized === 'localhost' ||
    normalized === '127.0.0.1' ||
    normalized === 'orrekie.local' ||
    normalized.endsWith('.orrekie.local')
  );
}

export function shouldConnectAuthEmulator(input: {
  useEmulators: string | undefined;
  appEnvironment: string | undefined;
  hostname: string | undefined;
}): boolean {
  if (input.useEmulators !== 'true') return false;
  const appEnvironment = (input.appEnvironment || '').toLowerCase();
  if (appEnvironment === 'staging' || appEnvironment === 'production') {
    return false;
  }
  if (!input.hostname) return false;
  return isLocalHost(input.hostname);
}

// Single source of truth for config (safe fallbacks ok; we ALWAYS connect emulator in DEV)
const cfg = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'dev-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'app.orrekie.local',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'orrekie-platform-staging',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:111111111111:web:devonly',
};

export function initFirebase() {
  if (!_app) {
    _app = (getApps()[0] as any) ?? initializeApp(cfg);
    
    // Safety boundary: DEV alone is insufficient for hosted deployments.
    // Emulator connections require explicit opt-in and local hostname.
    const hostname = typeof window !== 'undefined' ? window.location.hostname : undefined;
    const useAuthEmulator = shouldConnectAuthEmulator({
      useEmulators: import.meta.env.VITE_USE_EMULATORS as string | undefined,
      appEnvironment: import.meta.env.VITE_APP_ENVIRONMENT as string | undefined,
      hostname,
    });
    if (useAuthEmulator && !_didConnectEmu) {
      const host = (import.meta.env.VITE_AUTH_EMU_HOST as string) || '127.0.0.1';
      const port = (import.meta.env.VITE_AUTH_EMU_PORT as string) || '9099';
      try {
        connectAuthEmulator(getAuth(), `http://${host}:${port}`, { disableWarnings: true });
        _didConnectEmu = true;
        
        console.warn(`[Firebase Init] Auth emulator connected at http://${host}:${port}`);
      } catch (e) {
        
        console.warn('[Firebase Init] Skipped emulator connect:', e);
      }
    }
  }
  return _app!;
}

export const auth = () => getAuth();
